'use client';

import { c as _c } from "react/compiler-runtime";
import { useAnalyticsPageMeta } from '@/app/api/push-event/_helpers/useAnalyticsPageMeta.client';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { position } from '@/uiPrimitives/layout/position';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { pointerEventsNone } from '@/uiPrimitives/utility/pointerEvents.css';
import { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { compensateForDisclaimerWhiteSpace, measureInViewCSS, popupScrollArea, popupShadow } from './PageFeedback.css';
import { ClosePopup } from './ClosePopup';
import { usePathname } from 'next/navigation';
import { pushFeedbackPopupClosedEvent, pushFeedbackPopupOpenedEvent } from '@/globals/analytics/events/pushFeedbackEvent';
import { pageGrid } from '@/uiPrimitives/layout/pageGrid/pageGrid';
import { unprintable } from '@/uiPrimitives/base/printable.css';
import { hidden } from '@/uiPrimitives/utility/display.css';
import { viewTransitionName } from '@/uiPrimitives/utility/viewTransitionNames.css';
const PageFeedbackPopupContext = createContext<{
  onFieldChange: () => void;
  onSubmit: () => void;
  isPopping: boolean;
} | undefined>(undefined);
export const usePageFeedbackPopup = () => {
  const context = useContext(PageFeedbackPopupContext);
  if (context === undefined) {
    throw new Error("usePageFeedbackPopup must be used within a PageFeedbackPopupProvider");
  }
  return context;
};
const getPageCount = () => {
  try {
    const value = Number(sessionStorage.getItem('feedbackPageCounter'));
    if (isNaN(value)) return 0;
    return value;
  } catch (_error) {
    return 0;
  }
};
const savePageCount = (pageCount: number) => {
  try {
    sessionStorage.setItem('feedbackPageCounter', String(pageCount));
  } catch (_error) {
    // NOOP
  }
};

/**
 * Count on every page, not just ones with feedback
 */
export const useIncrementFeedbackPageCount = () => {
  const $ = _c(3);
  const savedForPageRef = useRef(undefined);
  const pathname = usePathname();
  let t0;
  let t1;
  if ($[0] !== pathname) {
    t0 = () => {
      if (savedForPageRef.current === pathname) {
        return;
      }
      savedForPageRef.current = pathname;
      savePageCount(getPageCount() + 1);
    };
    t1 = [pathname];
    $[0] = pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
};
const getHasBeenClosed = () => {
  try {
    const value = sessionStorage.getItem('feedbackHasBeenClosed');
    return value === 'true';
  } catch (_error) {
    return false;
  }
};
const saveHasBeenClosed = (hasBeenClosed: boolean) => {
  const value = hasBeenClosed ? 'true' : 'false';
  try {
    sessionStorage.setItem('feedbackHasBeenClosed', value);
  } catch (_error) {
    // NOOP
  }
};
export const PageFeedbackPopupProvider = t0 => {
  const $ = _c(33);
  const {
    children,
    popupEnabled,
    className
  } = t0;
  const measureInViewRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (measureInViewRef.current == null) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        setIsInView(entries[0]?.isIntersecting ?? false);
      });
      observer.observe(measureInViewRef.current);
      return () => {
        observer.disconnect();
      };
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(t1);
  const [isPopping, setIsPopping] = useState(false);
  const {
    wordCount
  } = useAnalyticsPageMeta();
  const scrollRef = useRef(null);
  const wrapperRef = useRef(null);
  let t2;
  let t3;
  if ($[1] !== popupEnabled || $[2] !== wordCount) {
    t2 = () => {
      const pageCanShowPopup = () => {
        if (wordCount == null || wordCount < 400) {
          return false;
        }
        if (!popupEnabled) {
          return false;
        }
        if (getPageCount() < 3) {
          return false;
        }
        if (getHasBeenClosed()) {
          return false;
        }
        return true;
      };
      const isFeedbackInView = () => {
        const boundingRect = wrapperRef.current?.getBoundingClientRect();
        if (boundingRect == null) {
          return false;
        }
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const inView = boundingRect.top < windowHeight && boundingRect.bottom > 0 && boundingRect.left < windowWidth && boundingRect.right > 0;
        return inView;
      };
      if (!pageCanShowPopup()) {
        return;
      }
      const showPopup = () => {
        if (!pageCanShowPopup()) {
          return;
        }
        if (isFeedbackInView()) {
          return;
        }
        savePageCount(0);
        setIsPopping(true);
        cleanUp();
        pushFeedbackPopupOpenedEvent();
      };
      const timer = setTimeout(showPopup, 120000);
      const observer_0 = new IntersectionObserver(entries_0 => {
        if (entries_0[0]?.isIntersecting) {
          showPopup();
        }
      }, {});
      if (scrollRef.current != null) {
        observer_0.observe(scrollRef.current);
      }
      const cleanUp = () => {
        observer_0.disconnect();
        clearTimeout(timer);
      };
      return cleanUp;
    };
    t3 = [popupEnabled, wordCount];
    $[1] = popupEnabled;
    $[2] = wordCount;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const onFieldChange = _temp;
  const onSubmit = _temp2;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      saveHasBeenClosed(true);
      savePageCount(0);
      setIsPopping(false);
      pushFeedbackPopupClosedEvent();
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onClose = t4;
  let t5;
  if ($[6] !== isPopping) {
    t5 = {
      onFieldChange,
      onSubmit,
      isPopping
    };
    $[6] = isPopping;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <i ref={scrollRef} className={classes(position({
      position: "absolute",
      insetInline: "0",
      insetBlockStart: "50%",
      insetBlockEnd: "0"
    }), pointerEventsNone)} />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== className || $[10] !== isInView || $[11] !== isPopping) {
    t7 = classes(className, unprintable, pageGrid({}), compensateForDisclaimerWhiteSpace, viewTransitionName({
      viewTransitionName: "page-feedback"
    }), ...(isPopping ? [position({
      position: "sticky",
      insetBlockEnd: "-1px"
    }), zIndex({
      zIndex: "feedback"
    }), !isInView && popupShadow] : [zIndex({
      isolation: "isolate"
    })]));
    $[9] = className;
    $[10] = isInView;
    $[11] = isPopping;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = classes(pageGrid({}), position({
      position: "relative"
    }));
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = (!isPopping || isInView) && hidden;
  let t10;
  if ($[14] !== t9) {
    t10 = classes(t9);
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10) {
    t11 = <ClosePopup onClick={onClose} className={t10} />;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== isPopping) {
    t12 = classes(pageGrid({}), isPopping && popupScrollArea);
    $[18] = isPopping;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== children || $[21] !== t12) {
    t13 = <div className={t12}>{children}</div>;
    $[20] = children;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <i className={classes(measureInViewCSS)} ref={measureInViewRef} />;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t11 || $[25] !== t13) {
    t15 = <div className={t8}>{t11}{t13}{t14}</div>;
    $[24] = t11;
    $[25] = t13;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t15 || $[28] !== t7) {
    t16 = <div ref={wrapperRef} className={t7}>{t15}</div>;
    $[27] = t15;
    $[28] = t7;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t16 || $[31] !== t5) {
    t17 = <PageFeedbackPopupContext.Provider value={t5}>{t6}{t16}</PageFeedbackPopupContext.Provider>;
    $[30] = t16;
    $[31] = t5;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
};
function _temp() {
  savePageCount(0);
}
function _temp2() {
  saveHasBeenClosed(true);
  savePageCount(0);
}